<script setup lang = "ts">

import {computed, type PropType, ref} from "vue";
import PriceInput from "~/components/common/input/PriceInput.vue";

const props = defineProps({
	price: {type: Number, required: true},
	type: {type: Object as PropType<"real-estate" | "car">, required: true},
	term: {type: Number},
	interest: {type: Number},
	deposit: {type: Number},
	isUsed: {type: Boolean}
})

const currentInterestRate = () => {
	switch (props.type) {
		case 'car':
			return !!props.isUsed ? 7.85 : 6.34
		case 'real-estate':
			return 7.06
	}
}

const getDeposit = () => {
	switch (props.type) {
		case 'car':
			return props.price * 0.1
		case 'real-estate':
			return 7.06
	}
}
const currentLoanTerm = () => {
	switch (props.type) {
		case 'car':
			return 70
		case 'real-estate':
			return 30
	}
}

const loanAmount = ref(props.price);
const downPayment = ref(props.deposit ?? getDeposit());
const interestRate = ref(props.interest ?? currentInterestRate());
const loanTerm = ref(props.term ?? currentLoanTerm());
const monthlyPayment = ref<number | null>(null);
const formatNumber = useNumberFormat()
const financialBalance = computed(() => {
	return loanAmount.value - downPayment.value
})

const loanValue = computed<Number>((): number => {
	const principal = loanAmount.value - downPayment.value;
	const monthlyInterestRate = interestRate.value / 100 / 12;
	const numberOfPayments = props.type === 'real-estate' ? loanTerm.value * 12 : loanTerm.value; // Convertimos el plazo en meses
	
	if (monthlyInterestRate === 0) {
		return numberOfPayments !== 0 ? (principal / numberOfPayments) : 0;
	} else {
		const discountFactor = 1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments);
		if (discountFactor !== 0) {
			return ((principal * monthlyInterestRate) / discountFactor);
		} else {
			return 0;
		}
	}
	
	
})

const getTitle = computed(() => {
	switch (props.type) {
		case 'car':
			return "Préstamo"
		case 'real-estate':
			return "Hipoteca"
	}
})

const getSpect = computed(() => {
	switch (props.type) {
		case 'car':
			return "del carro"
		case 'real-estate':
			return "de la propiedad"
	}
})
</script>

<template>
	<div class = "bg-gray-100 px-5 pt-2 pb-8 rounded-lg">
		<p class = "text-2xl my-5 font-bold mt-5">Calcula tu {{ getTitle }}</p>
		<div class = "p-5 rounded-lg bg-white mb-5">
			<div class = "border-l border-l-solid border-l-4 border-primary">
				<p class = "text-4xl font-bold pl-3">
					$ {{ formatNumber.formatDecimal((loanValue as number ?? 0)) }}
					<span class = "font-normal text-2xl">/mes</span>
				</p>
				<p v-if = "type=='real-estate'" class = "ml-3 pt-1 text-caption text-grey-7 font-light">
					Total anual: $ {{ formatNumber.formatDecimal((loanValue as number ?? 0) * 12) }} USD
				</p>
				<p class = "text-xs font-light pl-3">
					Pago mensual basado en parámetros
					ingresados.
				</p>
			</div>
		</div>
		<q-form ref = "form">
			<div class = "grid grid-cols-4 gap-x-3 gap-y-5 font-light text-base text-primary items-center">
				<PriceInput dense
										color = "dark-accent"
										:label = "`Costo ${getSpect}`"
										classInput = "broder-solid border px-5 border-gray-100 bg-white col-span-2 rounded-lg"
										square
										borderless
										suffix = "USD"
										v-model = "loanAmount"/>
				<PriceInput dense
										color = "dark-accent"
										classInput = "broder-solid border px-5 border-gray-100 bg-white col-span-2 rounded-lg"
										label = "Pronto"
										input-class = "rounded-lg"
										square
										borderless
										suffix = "USD"
										v-model = "downPayment"/>
				<q-input dense
								 color = "dark-accent"
								 borderless
								 class = "px-5 col-span-2 bg-white rounded-lg"
								 classInput = "broder-solid border px-5 border-gray-100 bg-white col-span-2 rounded-lg"
								 square
								 :label = "`Interés`"
								 suffix = "%"
								 v-model = "interestRate"/>
				<q-input dense
								 color = "dark-accent"
								 borderless
								 class = "broder-solid border px-5 border-gray-100 bg-white col-span-2 rounded-lg"
								 input-class = "rounded-lg"
								 label = "Término"
								 square
								 :suffix = "type == 'real-estate' ? 'años' : 'meses' "
								 v-model = "loanTerm"/>
				<PriceInput dense
										color = "dark-accent"
										borderless
										classInput = "broder-solid border px-5 border-gray-100 bg-white col-span-2 rounded-lg"
										input-class = "rounded-lg"
										square
										suffix = "USD"
										:readonly = "true"
										label = "Balance a Financiar"
										v-model = "financialBalance"/>
				<q-btn class = "col-span-2" outline color = "primary">Calcular {{ getTitle }}</q-btn>
			</div>
		</q-form>
	</div>
</template>

<style scoped>

</style>